import React, { useState, useEffect } from 'react';

/**
 * DomainsWheel - Interactive 13-petal wheel visualization
 * Based on the 13 Domains of Patient Experience from brand deck (page 7)
 * Enhanced with animations and click-to-explore journey functionality
 */
export const DomainsWheel = ({
  size = 400,
  interactive = true,
  showJourneyOnClick = false,
  onDomainClick = null
}) => {
  const [hoveredDomain, setHoveredDomain] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [isAnimating, setIsAnimating] = useState(true);

  // 13 domains with brand colors and journey descriptions
  const domains = [
    {
      name: 'Diagnosis & Symptom Onset',
      color: '#EBA09B',
      journey: 'The path from first symptoms to receiving a diagnosis—confusion, waiting, and finally clarity.'
    },
    {
      name: 'Symptoms',
      color: '#F5B9A5',
      journey: 'Living with daily symptoms—the visible and invisible challenges patients navigate.'
    },
    {
      name: 'Treatment Experience',
      color: '#E8A598',
      journey: 'From medication side effects to therapy sessions—how treatments affect daily life.'
    },
    {
      name: 'Healthcare System & Providers',
      color: '#F5D7AA',
      journey: 'Navigating appointments, insurance, and building relationships with care teams.'
    },
    {
      name: 'Functioning & Daily Life',
      color: '#FAE5C0',
      journey: 'How conditions impact work, relationships, and everyday activities.'
    },
    {
      name: 'Emotional Well-Being',
      color: '#F5C4C0',
      journey: 'The psychological journey—from fear and frustration to hope and acceptance.'
    },
    {
      name: 'Support & Resources',
      color: '#E8B4B8',
      journey: 'Finding community, connecting with others, and accessing helpful resources.'
    },
    {
      name: 'Identity, Background & Culture',
      color: '#D4A5A5',
      journey: 'How cultural identity shapes health beliefs, treatment choices, and care preferences.'
    },
    {
      name: 'Technology & Data',
      color: '#C5D4B8',
      journey: 'Using apps, devices, and digital tools to manage health and track progress.'
    },
    {
      name: 'Information & Communication',
      color: '#D4E0C8',
      journey: 'How patients find, understand, and share health information with others.'
    },
    {
      name: 'Environment & Equity',
      color: '#B8D4D4',
      journey: 'Access to care, geographic factors, and systemic barriers patients face.'
    },
    {
      name: 'Lifestyle & Health Behaviors',
      color: '#C8E0DC',
      journey: 'Diet, exercise, sleep, and daily choices that impact health outcomes.'
    },
    {
      name: 'Research & Clinical Trials',
      color: '#A8C5C5',
      journey: 'Considering, participating in, and contributing to medical research.'
    },
  ];

  const centerX = size / 2;
  const centerY = size / 2;
  const innerRadius = size * 0.15;
  const outerRadius = size * 0.42;

  // Pause animation on hover
  useEffect(() => {
    if (hoveredDomain !== null || selectedDomain !== null) {
      setIsAnimating(false);
    } else {
      const timer = setTimeout(() => setIsAnimating(true), 500);
      return () => clearTimeout(timer);
    }
  }, [hoveredDomain, selectedDomain]);

  // Generate petal path for each domain
  const generatePetal = (index) => {
    const anglePerPetal = (2 * Math.PI) / domains.length;
    const startAngle = index * anglePerPetal - Math.PI / 2;
    const endAngle = startAngle + anglePerPetal;
    const midAngle = startAngle + anglePerPetal / 2;

    // Inner arc points
    const innerStart = {
      x: centerX + innerRadius * Math.cos(startAngle),
      y: centerY + innerRadius * Math.sin(startAngle),
    };
    const innerEnd = {
      x: centerX + innerRadius * Math.cos(endAngle),
      y: centerY + innerRadius * Math.sin(endAngle),
    };

    // Outer arc points (slightly curved outward for petal shape)
    const petalTip = {
      x: centerX + outerRadius * Math.cos(midAngle),
      y: centerY + outerRadius * Math.sin(midAngle),
    };

    // Control points for the petal curves
    const controlRadius = outerRadius * 0.85;
    const controlAngle1 = startAngle + anglePerPetal * 0.25;
    const controlAngle2 = startAngle + anglePerPetal * 0.75;

    const control1 = {
      x: centerX + controlRadius * Math.cos(controlAngle1),
      y: centerY + controlRadius * Math.sin(controlAngle1),
    };
    const control2 = {
      x: centerX + controlRadius * Math.cos(controlAngle2),
      y: centerY + controlRadius * Math.sin(controlAngle2),
    };

    // Create petal path
    return `
      M ${innerStart.x} ${innerStart.y}
      Q ${control1.x} ${control1.y} ${petalTip.x} ${petalTip.y}
      Q ${control2.x} ${control2.y} ${innerEnd.x} ${innerEnd.y}
      A ${innerRadius} ${innerRadius} 0 0 0 ${innerStart.x} ${innerStart.y}
    `;
  };

  const handlePetalClick = (index) => {
    if (!interactive) return;

    if (showJourneyOnClick) {
      setSelectedDomain(selectedDomain === index ? null : index);
    }

    if (onDomainClick) {
      onDomainClick(domains[index]);
    }
  };

  // Calculate font size based on wheel size
  const centerFontSize = Math.max(12, size * 0.035);

  return (
    <div style={{ position: 'relative', width: size, height: size + (showJourneyOnClick ? 120 : 60), margin: '0 auto' }}>
      {/* CSS for animations */}
      <style>{`
        @keyframes gentlePulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
        @keyframes subtleRotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .domains-wheel-container {
          animation: ${isAnimating ? 'gentlePulse 4s ease-in-out infinite' : 'none'};
        }
        .petal-hover:hover {
          filter: brightness(1.1) !important;
        }
      `}</style>

      <div className="domains-wheel-container" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{ overflow: 'visible' }}
        >
          <defs>
            {/* Subtle drop shadow */}
            <filter id="petalShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.15" />
            </filter>
            {/* Glow effect for selected */}
            <filter id="petalGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>

          {/* Outer glow ring animation */}
          <circle
            cx={centerX}
            cy={centerY}
            r={outerRadius + 10}
            fill="none"
            stroke="rgba(235, 160, 155, 0.2)"
            strokeWidth="2"
            style={{
              animation: isAnimating ? 'subtleRotate 30s linear infinite' : 'none',
              strokeDasharray: '10 20',
            }}
          />

          {/* Petals */}
          {domains.map((domain, index) => (
            <path
              key={domain.name}
              d={generatePetal(index)}
              fill={domain.color}
              stroke={selectedDomain === index ? '#272726' : '#fff'}
              strokeWidth={selectedDomain === index ? '3' : '2'}
              className="petal-hover"
              style={{
                cursor: interactive ? 'pointer' : 'default',
                transition: 'all 0.3s ease',
                transform: hoveredDomain === index || selectedDomain === index ? 'scale(1.08)' : 'scale(1)',
                transformOrigin: `${centerX}px ${centerY}px`,
                filter: selectedDomain === index ? 'url(#petalGlow)' : 'url(#petalShadow)',
                opacity: (hoveredDomain !== null && hoveredDomain !== index) ||
                         (selectedDomain !== null && selectedDomain !== index) ? 0.5 : 1,
              }}
              onMouseEnter={() => interactive && setHoveredDomain(index)}
              onMouseLeave={() => interactive && setHoveredDomain(null)}
              onClick={() => handlePetalClick(index)}
            />
          ))}

          {/* Center circle */}
          <circle
            cx={centerX}
            cy={centerY}
            r={innerRadius}
            fill="#f7f5f3"
            stroke="#fff"
            strokeWidth="3"
            style={{
              filter: 'url(#petalShadow)',
            }}
          />

          {/* Center text */}
          <text
            x={centerX}
            y={centerY - centerFontSize * 0.4}
            textAnchor="middle"
            fill="#272726"
            fontSize={centerFontSize}
            fontWeight="600"
            fontFamily="'Source Serif Pro', Georgia, serif"
          >
            Patient
          </text>
          <text
            x={centerX}
            y={centerY + centerFontSize * 0.6}
            textAnchor="middle"
            fill="#272726"
            fontSize={centerFontSize}
            fontWeight="600"
            fontFamily="'Source Serif Pro', Georgia, serif"
          >
            Experience
          </text>
        </svg>
      </div>

      {/* Tooltip for hover */}
      {hoveredDomain !== null && selectedDomain === null && (
        <div
          style={{
            position: 'absolute',
            top: size / 2,
            left: size / 2,
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(39, 39, 38, 0.95)',
            color: '#fff',
            padding: '12px 18px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 10,
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          }}
        >
          {domains[hoveredDomain].name}
          {showJourneyOnClick && (
            <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '4px' }}>
              Click to explore
            </div>
          )}
        </div>
      )}

      {/* Journey panel for selected domain */}
      {showJourneyOnClick && selectedDomain !== null && (
        <div
          style={{
            marginTop: '20px',
            padding: '20px 24px',
            backgroundColor: domains[selectedDomain].color,
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            animation: 'fadeIn 0.3s ease',
          }}
        >
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(-10px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h4
                style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#272726',
                  marginBottom: '8px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                }}
              >
                {domains[selectedDomain].name}
              </h4>
              <p
                style={{
                  fontSize: '14px',
                  color: 'rgba(39, 39, 38, 0.85)',
                  lineHeight: '1.5',
                  margin: 0,
                }}
              >
                {domains[selectedDomain].journey}
              </p>
            </div>
            <button
              onClick={() => setSelectedDomain(null)}
              style={{
                background: 'rgba(39, 39, 38, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginLeft: '12px',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#272726" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Caption */}
      {!showJourneyOnClick && (
        <p
          style={{
            textAlign: 'center',
            fontSize: '14px',
            color: '#888',
            marginTop: '20px',
            fontStyle: 'italic',
          }}
        >
          We listen across 13 dimensions of health experience
        </p>
      )}

      {/* Interactive hint for journey mode */}
      {showJourneyOnClick && selectedDomain === null && (
        <p
          style={{
            textAlign: 'center',
            fontSize: '13px',
            color: '#888',
            marginTop: '16px',
          }}
        >
          Click any petal to explore that dimension of patient experience
        </p>
      )}
    </div>
  );
};

export default DomainsWheel;
